import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import TopHeader from './TopHeader';
import { useHeartbeat } from '@/hooks/useHeartbeat';
import { useAuth } from '@/contexts/AuthContext';
import MaintenancePage from '@/pages/MaintenancePage';
import PasskeyPrompt from '@/components/PasskeyPrompt';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function DashboardLayout({ children }) {
  const { i18n } = useTranslation();
  const { user, hasPerm } = useAuth();
  const isRTL = i18n.language === 'ar';
  const [maintenance, setMaintenance] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [version, setVersion] = useState(null);

  const disableMaintenance = async () => {
    setDisabling(true);
    try {
      const r = await api.post('/api/maintenance/toggle');
      setMaintenance(r.data.maintenance_mode);
      toast.success('Maintenance mode turned off');
    } catch {
      toast.error('Could not turn off maintenance mode');
    } finally {
      setDisabling(false);
    }
  };

  useEffect(() => {
    api.get('/api/version').then(r => setVersion(r.data)).catch(() => {});
  }, []);

  // Send heartbeat every 30s
  useHeartbeat();

  // Check maintenance mode on mount and every 30s
  useEffect(() => {
    const check = () => {
      api.get('/api/maintenance/status').then(r => setMaintenance(r.data.maintenance_mode)).catch(() => {});
    };
    check();
    const id = setInterval(check, 30000);
    return () => clearInterval(id);
  }, []);

  // If maintenance mode and user is not admin, show maintenance page
  if (maintenance && user?.role !== 'admin') {
    return <MaintenancePage />;
  }

  return (
    <div className={`min-h-screen bg-[var(--page)] ${isRTL ? 'font-arabic' : 'font-sans'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Sidebar wrapper — on desktop, wrapped in a card with margin */}
      <div className={`hidden lg:block fixed top-0 ${isRTL ? 'right-0' : 'left-0'} h-full z-50`}>
        <div className="h-full w-64">
          <Sidebar />
        </div>
      </div>
      {/* Mobile sidebar renders its own fixed overlay */}
      <div className="lg:hidden"><Sidebar /></div>
      <div className={`${isRTL ? 'lg:mr-64' : 'lg:ml-64'} min-h-screen flex flex-col`}>
        <main className="flex-1">
          <div className="px-4 pt-4 lg:pt-7 lg:pe-9 lg:ps-3 max-w-[1560px]">
            <TopHeader />
            <div className="h-12 lg:hidden" /> {/* Spacer for mobile hamburger menu */}
            {maintenance && user?.role === 'admin' && (
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-4 py-2 flex items-center justify-center gap-3 rounded-xl mb-4">
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0" />
                <span className="text-xs font-medium text-amber-700 dark:text-amber-400">Maintenance mode is active — only admins can access the platform</span>
                {hasPerm('toggle_maintenance') && (
                  <button
                    data-testid="disable-maintenance-btn"
                    disabled={disabling}
                    onClick={disableMaintenance}
                    className="shrink-0 text-xs font-semibold px-3 py-1 rounded-full bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60 transition-colors"
                  >
                    {disabling ? 'Turning off…' : 'Turn off'}
                  </button>
                )}
              </div>
            )}
            {children}
          </div>
        </main>
        <footer className="py-3 px-4 text-center">
          <p className="text-[11px] text-gray-400 dark:text-gray-600" data-testid="footer-version">
            © {new Date().getFullYear()} Inception. All rights reserved.
            {version && <span className="mx-1.5 text-gray-300 dark:text-gray-700">·</span>}
            {version && <span className="font-mono">v{version.version}</span>}
          </p>
        </footer>
        <PasskeyPrompt />
      </div>
    </div>
  );
}
